// ---- Größenfinder: size recommendation wizard ----
//
// Scoring logic: each candidate size accumulates points from three signals (body measurements,
// most commonly bought size, fit preference). See the "PUNKTESYSTEM" table supplied by the client
// for the exact point values below.

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

const SIZE_TABLES = {
  // Oberteile (inkl. Hemden)
  tops: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 55, weightMax: 68 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 63, weightMax: 75 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 70, weightMax: 85 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 80, weightMax: 95 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 90, weightMax: 105 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 100, weightMax: 115 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 110, weightMax: 130 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 120, weightMax: 145 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 135, weightMax: 160 }
  ],
  // Jacken & Mäntel
  outerwear: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 58, weightMax: 70 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 65, weightMax: 78 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 72, weightMax: 88 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 82, weightMax: 98 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 92, weightMax: 108 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 102, weightMax: 120 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 112, weightMax: 135 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 125, weightMax: 150 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 140, weightMax: 165 }
  ],
  // Unterteile
  bottoms: [
    { size: 'XS', heightMin: 165, heightMax: 173, weightMin: 52, weightMax: 65 },
    { size: 'S', heightMin: 170, heightMax: 178, weightMin: 60, weightMax: 73 },
    { size: 'M', heightMin: 175, heightMax: 183, weightMin: 68, weightMax: 82 },
    { size: 'L', heightMin: 180, heightMax: 188, weightMin: 78, weightMax: 92 },
    { size: 'XL', heightMin: 183, heightMax: 191, weightMin: 88, weightMax: 102 },
    { size: 'XXL', heightMin: 185, heightMax: 194, weightMin: 98, weightMax: 112 },
    { size: '3XL', heightMin: 187, heightMax: 205, weightMin: 108, weightMax: 130 },
    { size: '4XL', heightMin: 189, heightMax: 200, weightMin: 120, weightMax: 145 },
    { size: '5XL', heightMin: 190, heightMax: 205, weightMin: 135, weightMax: 160 }
  ]
};

function distancesFromMeasurement(value, table, minKey, maxKey) {
  // A size counts as a direct match (distance 0) whenever the value falls inside its own
  // table range — no averaging/center comparison between overlapping ranges.
  const matches = table.reduce((acc, row, i) => {
    if (value >= row[minKey] && value <= row[maxKey]) acc.push(i);
    return acc;
  }, []);

  if (matches.length) {
    return table.map((row, i) => Math.min(...matches.map(m => Math.abs(i - m))));
  }

  if (value < table[0][minKey]) return table.map((row, i) => i);
  if (value > table[table.length - 1][maxKey]) return table.map((row, i) => table.length - 1 - i);

  let nearest = 0;
  let nearestDist = Infinity;
  table.forEach((row, i) => {
    const dist = value < row[minKey] ? row[minKey] - value : value - row[maxKey];
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = i;
    }
  });
  return table.map((row, i) => Math.abs(i - nearest));
}

function pointsForDistance(dist, exact, oneOff, twoOff, threeOrMoreOff) {
  if (dist === 0) return exact;
  if (dist === 1) return oneOff;
  if (dist === 2) return twoOff;
  return threeOrMoreOff;
}

function scoreSizes(state) {
  const table = SIZE_TABLES[state.table];
  const heightDistances = distancesFromMeasurement(state.height, table, 'heightMin', 'heightMax');
  const weightDistances = distancesFromMeasurement(state.weight, table, 'weightMin', 'weightMax');
  const commonIdx = SIZE_ORDER.indexOf(state.commonSize);

  const baseScores = table.map((row, i) => {
    const commonDist = commonIdx === -1 ? null : Math.abs(i - commonIdx);

    let score = pointsForDistance(heightDistances[i], 6, 2, 1, 0) + pointsForDistance(weightDistances[i], 6, 2, 1, 0);
    if (commonDist !== null) score += pointsForDistance(commonDist, 8, 4, 0, 0);
    return score;
  });

  let leader = 0;
  baseScores.forEach((value, i) => {
    if (value > baseScores[leader]) leader = i;
  });

  let favored = leader;
  let penalized = null;
  if (state.fit === 'tight') {
    favored = leader - 1;
    penalized = leader + 1;
  } else if (state.fit === 'loose') {
    favored = leader + 1;
    penalized = leader - 1;
  }

  const finalScores = baseScores.map((value, i) => {
    if (i === favored) return value + 2;
    if (i === penalized) return value - 2;
    return value;
  });

  return table
    .map((row, i) => ({
      size: row.size,
      points: finalScores[i],
      index: i,
      percent: Math.max(0, Math.min(100, Math.round((finalScores[i] * 100) / 22)))
    }))
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      // Tie-break: the size favored by the fit preference (question 3) wins
      if (a.index === favored) return -1;
      if (b.index === favored) return 1;
      return 0;
    });
}

// ---- Variant resolution ----

function findVariantForSize(config, sizeLabel) {
  const variantPicker = document.querySelector(`variant-picker[section-id="${CSS.escape(config.sectionId)}"]`);
  const currentVariant = variantPicker?.selectedVariant || config.variants.find(v => v.available) || config.variants[0];
  const sizeKey = `option${config.sizeOptionPosition}`;

  const matchesOtherOptions = variant => {
    for (let position = 1; position <= 3; position += 1) {
      if (position === config.sizeOptionPosition) continue;
      const key = `option${position}`;
      if (currentVariant && variant[key] !== currentVariant[key]) return false;
    }
    return true;
  };

  const exact = config.variants.find(
    v => matchesOtherOptions(v) && (v[sizeKey] || '').trim().toUpperCase() === sizeLabel.toUpperCase()
  );
  if (exact) return exact;

  // Fall back to the nearest carried size (in SIZE_ORDER distance) if the recommended one isn't sold
  const targetIdx = SIZE_ORDER.indexOf(sizeLabel);
  if (targetIdx === -1) return null;

  const candidates = config.variants
    .filter(matchesOtherOptions)
    .map(v => ({ variant: v, idx: SIZE_ORDER.indexOf((v[sizeKey] || '').trim().toUpperCase()) }))
    .filter(c => c.idx !== -1)
    .sort((a, b) => Math.abs(a.idx - targetIdx) - Math.abs(b.idx - targetIdx));

  return candidates[0]?.variant || null;
}

async function addVariantToCart(variantId) {
  const sectionsToBundle = ['variant-added'];

  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections: sectionsToBundle }
  }));

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');
  formData.set('sections', sectionsToBundle.join(','));
  formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    body: formData,
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });
  if (!response.ok) return false;

  const responseJson = await response.json();
  const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
  cartContent['sections'] = responseJson['sections'];

  document.dispatchEvent(new CustomEvent('variant:add', {
    bubbles: true,
    detail: { items: Array.isArray(responseJson.items) ? responseJson.items : [responseJson], cart: cartContent }
  }));

  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'variant:add', cart: cartContent }
  }));

  return true;
}

// ---- Wizard controller ----

class SizeFinderInstance {
  constructor(root) {
    this.root = root;
    this.config = JSON.parse(root.querySelector('[data-size-finder-config]')?.textContent || '{}');
    this.state = this.initialState();

    this.backBtn = root.querySelector('[data-size-finder-back]');
    this.nextBtn = root.querySelector('[data-size-finder-next]');
    this.wishlistBtn = root.querySelector('[data-size-finder-wishlist]');
    this.defaultNextLabel = this.nextBtn?.textContent || '';

    this.bindEvents();
  }

  initialState() {
    return { step: 1, category: null, categoryLabel: '', table: null, height: null, weight: null, commonSize: null, fit: null, autoDetected: false };
  }

  bindEvents() {
    this.root.querySelectorAll('[data-size-finder-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    this.root.querySelectorAll('[data-category]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('[data-category]').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.state.category = btn.dataset.category;
        this.state.table = btn.dataset.table;
        this.state.categoryLabel = btn.dataset.label;
      });
    });

    this.root.querySelectorAll('[data-common-size]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('[data-common-size]').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.state.commonSize = btn.dataset.commonSize;
      });
    });

    this.root.querySelectorAll('[data-fit]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('[data-fit]').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.state.fit = btn.dataset.fit;
      });
    });

    const heightInput = this.root.querySelector('[data-field="height"]');
    const weightInput = this.root.querySelector('[data-field="weight"]');
    heightInput?.addEventListener('input', () => {
      this.state.height = heightInput.value ? parseFloat(heightInput.value) : null;
    });
    weightInput?.addEventListener('input', () => {
      this.state.weight = weightInput.value ? parseFloat(weightInput.value) : null;
    });

    this.backBtn?.addEventListener('click', () => this.goBack());
    this.nextBtn?.addEventListener('click', () => this.onNextClicked());
    this.wishlistBtn?.addEventListener('click', () => this.addRecommendedToWishlist());

    this._onKeydown = e => {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    };
  }

  isOpen() {
    return this.root.classList.contains('is-open');
  }

  getFocusableElements() {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.root.querySelectorAll(selector)).filter(el => el.offsetParent !== null);
  }

  trapFocus(e) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  open(triggerEl) {
    this.triggerEl = triggerEl || document.activeElement;
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    this.reset();
    document.documentElement.classList.add('lock');
    document.addEventListener('keydown', this._onKeydown);
  }

  close() {
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lock');
    document.removeEventListener('keydown', this._onKeydown);
    this.triggerEl?.focus();
  }

  reset() {
    this.state = this.initialState();
    this.result = null;

    // If the product's metafields resolve to a known category, skip the manual step 1.
    const autoBtn = this.config.autoCategory
      ? this.root.querySelector(`[data-category="${this.config.autoCategory}"]`)
      : null;
    (autoBtn || this.root.querySelector('[data-category]'))?.click();
    this.state.autoDetected = !!autoBtn;

    const dot1 = this.root.querySelector('[data-dot="1"]');
    if (dot1) dot1.hidden = this.state.autoDetected;

    this.root.querySelector('[data-common-size]')?.click();
    this.root.querySelector('[data-fit]')?.click();

    const heightInput = this.root.querySelector('[data-field="height"]');
    const weightInput = this.root.querySelector('[data-field="weight"]');
    if (heightInput) heightInput.value = '';
    if (weightInput) weightInput.value = '';
    this.state.height = null;
    this.state.weight = null;

    this.root.querySelectorAll('[data-step-error], [data-add-to-cart-error]').forEach(el => { el.hidden = true; });

    this.showStep(this.state.autoDetected ? 2 : 1);
  }

  showStep(step) {
    this.state.step = step;

    this.root.querySelectorAll('[data-step]').forEach(el => {
      el.hidden = Number(el.dataset.step) !== step;
    });

    this.root.querySelectorAll('[data-dot]').forEach(dot => {
      dot.classList.toggle('is-active', Number(dot.dataset.dot) === step);
    });

    if (this.backBtn) this.backBtn.hidden = step === 1 || (step === 2 && this.state.autoDetected);

    if (step === 5) {
      this.renderResult();
    } else if (this.nextBtn) {
      this.nextBtn.textContent = this.defaultNextLabel;
      if (this.wishlistBtn) this.wishlistBtn.hidden = true;
    }

    this.updateStepTitle(step);

    const titleEl = this.root.querySelector(`[data-step="${step}"] .size-finder-step__title`);
    titleEl?.focus({ preventScroll: true });
  }

  updateStepTitle(step) {
    if (step !== 3 && step !== 5) return;
    const titleEl = this.root.querySelector(`[data-step="${step}"] [data-template]`);
    if (!titleEl) return;
    titleEl.textContent = (titleEl.dataset.template || '').replace('%CATEGORY%', this.state.categoryLabel || '');
  }

  validateStep(step) {
    if (step === 1) return !!this.state.category;

    if (step === 2) {
      const valid = this.state.height >= 100 && this.state.height <= 230 && this.state.weight >= 30 && this.state.weight <= 250;
      const errorEl = this.root.querySelector('[data-step="2"] [data-step-error]');
      if (errorEl) errorEl.hidden = valid;
      return valid;
    }

    if (step === 3) return !!this.state.commonSize;
    if (step === 4) return !!this.state.fit;

    return true;
  }

  onNextClicked() {
    if (this.state.step === 5) {
      this.addRecommendedToCart();
      return;
    }
    this.goNext();
  }

  goNext() {
    if (!this.validateStep(this.state.step)) return;
    if (this.state.step < 5) this.showStep(this.state.step + 1);
  }

  goBack() {
    if (this.state.step > 1) this.showStep(this.state.step - 1);
  }

  renderResult() {
    this.result = scoreSizes(this.state).slice(0, 2);
    const container = this.root.querySelector('[data-result-container]');
    const i18n = window.sizeFinderI18n || {};

    if (container) {
      container.innerHTML = this.result
        .map((entry, i) => {
          const percentText = (i18n.matchPercent || 'Zu %PERCENT%% wird dir dieser Artikel in Größe %SIZE% passen')
            .replace('%PERCENT%', entry.percent)
            .replace('%SIZE%', entry.size);

          return `
            <div class="size-finder-result__row${i === 0 ? ' size-finder-result__row--primary' : ''}">
              <div class="size-finder-result__row-header">
                <span class="size-finder-result__size">${i18n.sizeLabel ? i18n.sizeLabel + ' ' : 'Größe '}${entry.size}</span>
                ${i === 0 ? `<span class="badge">${i18n.recommendationBadge || 'Empfehlung'}</span>` : ''}
              </div>
              <div class="size-finder-result__bar-row">
                <div class="size-finder-result__bar"><div class="size-finder-result__bar-fill" style="width:${entry.percent}%"></div></div>
                <span class="size-finder-result__percent">${entry.percent}%</span>
              </div>
              <p class="size-finder-result__percent-text text-sm text-subdued">${percentText}</p>
            </div>`;
        })
        .join('');
    }

    if (this.nextBtn) {
      const template = i18n.addToCart || 'Größe %SIZE% in den Warenkorb';
      this.nextBtn.textContent = template.replace('%SIZE%', this.result[0]?.size || '');
      this.nextBtn.disabled = false;
    }
    if (this.wishlistBtn) this.wishlistBtn.hidden = false;

    const errorEl = this.root.querySelector('[data-add-to-cart-error]');
    if (errorEl) errorEl.hidden = true;
  }

  async addRecommendedToCart() {
    const size = this.result?.[0]?.size;
    if (!size) return;

    const variant = findVariantForSize(this.config, size);
    const errorEl = this.root.querySelector('[data-add-to-cart-error]');

    if (!variant || !variant.available) {
      if (errorEl) errorEl.hidden = false;
      return;
    }

    this.nextBtn.disabled = true;
    try {
      await addVariantToCart(variant.id);
      this.close();
    } finally {
      this.nextBtn.disabled = false;
    }
  }

  addRecommendedToWishlist() {
    const toggle = document.querySelector(`[data-wishlist-toggle="${this.config.productHandle}"].wishlist-button--product`);
    toggle?.click();
  }
}

function initSizeFinder() {
  document.querySelectorAll('[data-size-finder]').forEach(root => {
    if (root._sizeFinderInstance) return;
    root._sizeFinderInstance = new SizeFinderInstance(root);
  });

  document.addEventListener('click', e => {
    const trigger = e.target.closest('[data-size-finder-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.sizeFinderOpen);
    modal?._sizeFinderInstance?.open(trigger);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeFinder);
} else {
  initSizeFinder();
}
